<template>
  <div class="column full-height">
    <header
      class="row items-center justify-between q-px-2x-md full-width no-wrap"
      :style="{ height: '86px' }"
    >
      <div class="column">
        <span class="text-lg text-neutral text-weight-medium">
          {{ creditNote?.number || "-" }}
        </span>
      </div>
      <div>
        <PBtn dense flat @click="openFilesDrawer">
          <DocumentIcon color="primary" class="icon--xs q-mr-xxs" />
          <span class="text-xs">
            Files ({{ creditNote?.cn_file?.length || 0 }})
          </span>
        </PBtn>
        <span>
          <PBadge
            :label="creditNote.status"
            :type="creditNote.status == 'Active' ? 'success' : 'Cancelled'"
            class="q-ml-sm q-mr-md text-xs"
          />
          <PTooltip
            v-if="
              creditNote.status === 'Cancelled' && creditNote?.reason?.length
            "
          >
            {{ creditNote.reason }}
          </PTooltip>
        </span>
        <PBtn color="primary" flat round @click="onClose">
          <CloseIcon class="icon" />
        </PBtn>
      </div>
    </header>
    <q-separator color="neutral-40" />
    <div class="col full-width overflow-y-scroll">
      <UserDetails :user-details="creditNote" :source="source" :module="'cn'" />
      <q-separator class="q-ma-xxl" />
      <div class="overflow-hidden q-mx-2x-md q-mt-xl">
        <legend class="q-mb-sm text-neutral text-weight-medium text-md">
          Items
        </legend>
        <PTable
          v-if="creditNote?.lineitems?.length"
          :pagination="{ rowsPerPage: 25 }"
          :hide-pagination="true"
          :rows-per-page-options="[]"
          :columns="productListColumns"
          :rows="creditNote.lineitems"
          :style="{ margin: '0 -15px', padding: 0 }"
          table-header-class="text-neutral-70"
          dense
          flat
          wrap-cells
        >
          <template #body-cell="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <PEditable v-model="cellProps.row[props.col.name]" disable />
            </q-td>
          </template>

          <template #body-cell-name="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <ItemNameCell :col="cellProps.col" :table-props="cellProps" />
            </q-td>
          </template>

          <template #body-cell-qty="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <ItemQuantityCell :col="cellProps.col" :table-props="cellProps" />
            </q-td>
          </template>

          <template #body-cell-rate="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <ItemRateCell :col="cellProps.col" :table-props="cellProps" />
            </q-td>
          </template>

          <template #body-cell-amount="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <ItemAmountCell :col="cellProps.col" :table-props="cellProps" />
            </q-td>
          </template>

          <template #body-cell-po_qty="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <div class="align-middle">
                <p class="q-mb-none">{{ roundOff(props.value) }}</p>
                <p class="q-mb-none">{{ props.row.po_unit }}</p>
              </div>
            </q-td>
          </template>

          <template #body-cell-conversion_factor="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <span
                v-if="cellProps.row.unit === cellProps.row.po_unit"
                class="ellipsis text-neutral-90 text-xs"
              >
                Same Units
              </span>
              <span v-else class="ellipsis text-neutral-90 text-xs">
                {{ props.row.is_po_unit_bigger ? props.value : 1 }}
                {{ props.row.unit || "Unit" }}
                =
                {{ props.row.is_po_unit_bigger ? 1 : props.value }}
                {{ props.row.po_unit || "Unit" }}
              </span>
            </q-td>
          </template>
          <template #body-cell-po_rate="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <span class="ellipsis text-neutral text-xs">
                {{ props.value }}
              </span>
            </q-td>
          </template>
        </PTable>

        <span
          v-if="creditNote?.is_freight_included"
          class="text-xxs text-neutral text-italic"
        >
          <sup>*</sup>Freight is included in the rate
        </span>
      </div>

      <div class="row q-px-2x-md q-mt-xl text-sm text-neutral">
        <div class="col-6">
          <div>
            <span class="text-neutral-80">Remarks </span>
          </div>
          <div>
            <span> {{ creditNote?.remarks || "" }} </span>
          </div>
        </div>
        <div class="col-6">
          <div class="row q-col-gutter-xs">
            <div class="col-6">
              <p class="q-mb-none">Total Amount</p>
              <span class="text-xs text-neutral-70"> Excludes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: getTotalAmountWithoutTax(),
                    style: "currency",
                  })
                }}
              </div>
            </div>

            <div class="col-6">
              <p class="q-mb-none">Tax Total</p>
              <span class="text-xs text-neutral-70"> GST + CESS </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: creditNote.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>

            <div class="col-6 q-mb-xs">
              <p class="q-mb-none">Grand Total</p>
              <span class="text-xs text-neutral-70"> Includes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: creditNote.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div v-if="source == 'sales'" class="row q-col-gutter-y-sm">
            <div v-if="creditNote?.tcs" class="col-6">
              <span>TCS({{ creditNote?.tcs?.percentage }})</span>
              <PTooltip
                anchor="bottom left"
                self="top left"
                :offset="[0, 1]"
                class="text-sm"
                max-width="500px"
              >
                {{ creditNote?.tcs?.name }}
              </PTooltip>
            </div>
            <div v-if="creditNote?.tcs" class="col-6">
              <div class="text-right">
                <span>
                  {{
                    numberFormat({
                      number: creditNote.tcs?.amount ?? 0,
                      style: "currency",
                    })
                  }}
                </span>
              </div>
            </div>
            <div class="col-6">
              <p>Remaining Credits</p>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: creditNote?.remaining_credits ?? 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div v-if="source == 'purchases'" class="row q-mt-xs">
            <div
              v-if="creditNote.seller?.tax_settings?.percentage"
              class="col-6 q-mb-md"
            >
              <TdsInfoTooltip
                :percentage="creditNote.seller?.tax_settings?.percentage"
              />
            </div>
            <div
              v-if="creditNote.seller?.tax_settings?.percentage"
              class="col-6"
            >
              <div class="text-right">
                {{ creditNote.seller?.tax_settings?.percentage ? "-" : "" }}
                {{
                  numberFormat({
                    number:
                      (getTotalAmountWithoutTax() *
                        creditNote.seller?.tax_settings?.percentage) /
                        100 || 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-6">
              <p>Balance Due</p>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: creditNote?.balance_due ?? 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <q-separator class="q-ma-xxl" />

      <div class="q-px-2x-md q-mt-xl q-pb-2x-xl">
        <AssociatedVouchers
          :id="creditNote?._id"
          :type="
            props.source === 'purchases'
              ? 'pb_cn'
              : props.source === 'sales'
                ? 'sl_cn'
                : ''
          "
        />
      </div>
    </div>
  </div>
</template>
<script setup>
import CloseIcon from "assets/icons/close.svg";
import DocumentIcon from "assets/icons/document.svg";
import { date } from "quasar";
import { numberFormat, roundOff } from "assets/scripts/utilities/helpers";
import PBadge from "src/components/PBadge.vue";
import UserDetails from "./UserDetails.vue";

const { formatDate } = date;
const { toggleCreditNoteDetailsDrawer } = useCreditNoteStore();
const { creditNote } = storeToRefs(useCreditNoteStore());
const { toggleAssociatedDataDrawer } = usePaymentRequestStore();
const { removeQueryParams } = useRouterUtilities();

const { viewFile } = useFile();

const props = defineProps({ source: { type: String, default: "" } });

const onClose = () => {
  removeQueryParams();

  toggleCreditNoteDetailsDrawer({ open: false });
};

const { getTotalAmountWithoutTax } = useProductList({ formData: creditNote });

const productListColumns = computed(() => {
  let columns = [
    { align: "left", name: "name", label: "Name", field: "name" },
    { align: "left", name: "qty", label: "Qty", field: "qty" },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
  ];

  return columns;
});

const status = useColor();

const filesColumns = computed(() => {
  return [
    {
      label: "Date",
      field: "created_date",
      format: (value) => {
        return formatDate(value, "DD MMM YYYY");
      },
    },
    {
      label: "Name",
      field: "file_name",
      cellClass: "text-primary text-weight-medium underline cursor-pointer",
      onClick: viewFile,
    },
    {
      align: "center",
      label: "Status",
      field: "status",
      format: (value) =>
        `<span class="bg-${status(value).color} text-${
          status(value).textColor
        } q-px-sm q-py-xxs rounded" title="Status">${value}</span>`,
      cellClass: "text-center q-px-sm q-py-xxs rounded",
    },
  ];
});

const openFilesDrawer = () => {
  toggleAssociatedDataDrawer({
    edit: false,
    level: 0,
    open: true,
    view: true,
    props: {
      columns: unref(filesColumns),
      rows: creditNote.value?.cn_file,
      heading: "Associated Files",
      type: "file",
    },
  });
};
</script>
